/* eslint-disable react/require-default-props */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import usePermissions from '../../../../components/Menu/MenuItems/hooks/usePermissions';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
   SectionActions,
} from '../../../../styles/layout/Section';
import { EmptyState } from '../../../../styles/feedback/EmptyState';
import { CtaButton, SmallButton } from '../../../../styles/ui/Button';
import CredentialStatusBadge from '../../components/CredentialStatusBadge';
import EnvironmentBadge from '../../components/EnvironmentBadge';
import ConfirmDialog from '../../components/ConfirmDialog';
import ConnectionTestResult from './ConnectionTestResult';
import CredentialFormModal from './CredentialFormModal';
import RevokeCredentialModal from './RevokeCredentialModal';
import { PROVIDER_LABELS } from '../../constants';
import { formatDateTimeBr } from '../../utils/format';
import { financeErrorMessage } from '../../utils/apiError';
import {
   activateCredential,
   testConnection,
} from '../../../../services/finance/integrationCredentialsApi';

// ---------------------------------------------------------------------------
// Credenciais de integração da conta (handoff 0079 §3.2 e §3.3).
//
// A chave NUNCA aparece: o que se exibe é a máscara ($aact_…9f3c), o ambiente e
// a situação. Não há "revelar" — perdeu, gera outra na ASAAS e ROTACIONA aqui.
//
// RBAC: quem tem só `credential:read` vê a credencial mascarada e NÃO vê os
// botões de rotacionar/revogar/reativar/testar — escondidos, não desabilitados
// (§2). Testar conexão exige `manage` porque decifra a chave e a apresenta ao
// provedor. Sem `credential:read`, a seção inteira não é renderizada (a decisão
// fica na ficha, que nem monta este componente).
// ---------------------------------------------------------------------------

const CredentialCard = styled.div`
   border: 1px solid
      ${({ $active, theme }) =>
         $active
            ? 'rgba(46,125,50,0.45)'
            : theme?.colors?.border ?? 'rgba(0,0,0,0.1)'};
   background: ${({ $active }) =>
      $active ? 'rgba(46,125,50,0.04)' : 'transparent'};
   border-radius: 10px;
   padding: 14px;

   & + & {
      margin-top: 10px;
   }
`;

const CardTop = styled.div`
   display: flex;
   justify-content: space-between;
   gap: 12px;
   flex-wrap: wrap;
`;

const CardInfo = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;
   min-width: 0;

   .title {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
      font-size: 0.9rem;
      font-weight: 700;
   }

   .mask {
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 0.95rem;
      letter-spacing: 0.02em;
   }

   .meta {
      font-size: 0.75rem;
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
   }
`;

const CardActions = styled.div`
   display: flex;
   gap: 6px;
   align-items: flex-start;
   flex-wrap: wrap;
`;

const Alert = styled.div`
   border: 1px solid rgba(133, 79, 11, 0.35);
   background: rgba(133, 79, 11, 0.08);
   color: #854f0b;
   border-radius: 10px;
   padding: 12px 14px;
   font-size: 12.5px;
   line-height: 1.55;
   margin-bottom: 14px;

   strong {
      font-weight: 800;
   }
`;

export default function CredentialsSection({
   bankAccountId,
   accountStatus,
   credentials,
   onChanged,
}) {
   const { hasPermission } = usePermissions();
   const canManage = hasPermission('finance:credential:manage');

   const [formOpen, setFormOpen] = useState(false);
   const [formMode, setFormMode] = useState('create');
   const [targetCredential, setTargetCredential] = useState(null);

   const [revokeOpen, setRevokeOpen] = useState(false);
   const [activateTarget, setActivateTarget] = useState(null);

   const [busyId, setBusyId] = useState(null);
   // Resultado do teste por credencial — vive só nesta sessão de tela.
   const [testResults, setTestResults] = useState({});

   const activeCredential = credentials.find((c) => c.key_status === 'active');
   const isAccountInactive = accountStatus === 'inactive';

   const openCreate = () => {
      setFormMode('create');
      setTargetCredential(null);
      setFormOpen(true);
   };

   const openRotate = (credential) => {
      setFormMode('rotate');
      setTargetCredential(credential);
      setFormOpen(true);
   };

   const openRevoke = (credential) => {
      setTargetCredential(credential);
      setRevokeOpen(true);
   };

   const handleActivate = async () => {
      if (!activateTarget || busyId) return;
      setBusyId(activateTarget.id);
      try {
         const { message } = await activateCredential(activateTarget.id);
         toast.success(message);
         setActivateTarget(null);
         onChanged?.();
      } catch (err) {
         toast.error(financeErrorMessage(err, 'Erro ao reativar a credencial.'));
      } finally {
         setBusyId(null);
      }
   };

   const handleTest = async (credential) => {
      if (busyId) return;
      setBusyId(credential.id);
      try {
         // A chamada vai à internet (o backend fala com a ASAAS) — daí o estado
         // de carregando no próprio botão.
         const test = await testConnection(credential.id);
         setTestResults((prev) => ({ ...prev, [credential.id]: test }));
      } catch (err) {
         // Só cai aqui em erro de verdade (404 / 5xx / rede). Chave recusada
         // volta 200 com ok:false e é renderizada como resultado negativo.
         toast.error(financeErrorMessage(err, 'Não foi possível testar a conexão.'));
      } finally {
         setBusyId(null);
      }
   };

   return (
      <Section>
         <SectionHeader>
            <SectionTitle>
               Credenciais de integração
               {credentials.length ? ` (${credentials.length})` : ''}
            </SectionTitle>
            {canManage && !activeCredential && (
               <SectionActions>
                  <CtaButton
                     type="button"
                     onClick={openCreate}
                     disabled={isAccountInactive}
                     title={
                        isAccountInactive
                           ? 'Reative a conta para cadastrar uma credencial'
                           : undefined
                     }
                  >
                     + Adicionar credencial
                  </CtaButton>
               </SectionActions>
            )}
         </SectionHeader>

         <SectionBody>
            {!activeCredential && (
               <Alert>
                  Esta conta <strong>não tem credencial ativa</strong> e, por
                  isso, <strong>não consegue emitir cobranças</strong>.
                  {isAccountInactive
                     ? ' A conta também está inativa — reative-a antes de cadastrar a chave.'
                     : ' Cadastre a chave de API gerada no painel da ASAAS e teste a conexão.'}
               </Alert>
            )}

            {credentials.length ? (
               credentials.map((credential) => {
                  const isActive = credential.key_status === 'active';
                  const isBusy = busyId === credential.id;

                  return (
                     <CredentialCard key={credential.id} $active={isActive}>
                        <CardTop>
                           <CardInfo>
                              <div className="title">
                                 <span>
                                    {PROVIDER_LABELS[credential.provider] ??
                                       credential.provider}
                                 </span>
                                 <EnvironmentBadge
                                    value={credential.environment}
                                 />
                                 <CredentialStatusBadge
                                    value={credential.key_status}
                                 />
                              </div>
                              <span className="mask">
                                 {credential.api_key_mask ?? '—'}
                              </span>
                              <div className="meta">
                                 <span>
                                    Cadastrada em{' '}
                                    {formatDateTimeBr(credential.created_at)}
                                 </span>
                                 <span>
                                    Última rotação:{' '}
                                    {credential.key_last_rotated_at
                                       ? formatDateTimeBr(
                                            credential.key_last_rotated_at,
                                         )
                                       : 'nunca'}
                                 </span>
                              </div>
                           </CardInfo>

                           {canManage && (
                              <CardActions>
                                 {isActive && (
                                    <>
                                       <SmallButton
                                          type="button"
                                          $intent="cta"
                                          onClick={() => handleTest(credential)}
                                          disabled={isBusy}
                                       >
                                          {isBusy ? 'Testando…' : 'Testar conexão'}
                                       </SmallButton>
                                       <SmallButton
                                          type="button"
                                          $intent="write"
                                          onClick={() => openRotate(credential)}
                                          disabled={isBusy}
                                       >
                                          Rotacionar
                                       </SmallButton>
                                       <SmallButton
                                          type="button"
                                          $intent="danger"
                                          onClick={() => openRevoke(credential)}
                                          disabled={isBusy}
                                       >
                                          Revogar
                                       </SmallButton>
                                    </>
                                 )}
                                 {!isActive && (
                                    <SmallButton
                                       type="button"
                                       $intent="write"
                                       onClick={() =>
                                          setActivateTarget(credential)
                                       }
                                       disabled={
                                          isBusy ||
                                          Boolean(activeCredential) ||
                                          isAccountInactive
                                       }
                                       title={
                                          activeCredential
                                             ? 'Já existe uma credencial ativa nesta conta'
                                             : undefined
                                       }
                                    >
                                       Reativar
                                    </SmallButton>
                                 )}
                              </CardActions>
                           )}
                        </CardTop>

                        <ConnectionTestResult
                           test={testResults[credential.id]}
                        />
                     </CredentialCard>
                  );
               })
            ) : (
               <EmptyState
                  title="Nenhuma credencial cadastrada"
                  description="Cadastre a chave de API da ASAAS para que esta conta possa emitir cobranças."
               />
            )}
         </SectionBody>

         <CredentialFormModal
            isOpen={formOpen}
            onClose={() => setFormOpen(false)}
            mode={formMode}
            bankAccountId={bankAccountId}
            credential={targetCredential}
            onSaved={onChanged}
         />

         <RevokeCredentialModal
            isOpen={revokeOpen}
            onClose={() => setRevokeOpen(false)}
            credential={targetCredential}
            onDone={onChanged}
         />

         <ConfirmDialog
            isOpen={Boolean(activateTarget)}
            onClose={() => setActivateTarget(null)}
            onConfirm={handleActivate}
            title="Reativar credencial"
            confirmLabel="Reativar"
            isBusy={Boolean(busyId)}
         >
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55 }}>
               A credencial voltará a ser usada para emitir cobranças nesta
               conta. Só é possível ter uma credencial ativa por provedor.
            </p>
         </ConfirmDialog>
      </Section>
   );
}

CredentialsSection.propTypes = {
   bankAccountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
   accountStatus: PropTypes.string,
   credentials: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
   onChanged: PropTypes.func,
};
